import type { FC, ReactNode } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { encode, isBlurhashValid } from "blurhash"
import {
	IconPhotoMinusOutline18 as ImageMinus,
	IconPhotoPlusOutline18 as PhotoPlusIcon,
	IconTrashOutline18 as TrashIcon,
	IconArrowUpFromLineOutline18 as UploadIcon,
	IconMagicWandOutline18 as Wand2,
} from "nucleo-ui-outline-18"
import { Blurhash } from "react-blurhash"

import {
	HIDDEN_ARTICLE_COVER,
	isArticleCoverHidden,
} from "@productlane/lib/src/article-cover"
import { cn, getClipboardFiles } from "@productlane/lib/src/browser"
import { Button, IconButton } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { ALLOWED_FILE_SIZE_IN_MB } from "@/constants"
import { api } from "@/lib/api"
import { readCachedImage } from "@/lib/cached-image"
import {
	COVER_HEIGHT,
	COVER_WIDTH,
	processCoverImage,
} from "@/lib/process-cover-image"
import { useRole } from "@/lib/useRole"
import { useZero } from "@/lib/zero"
import { CoverUploadBox } from "@/screens/app/changelog/generated-cover-button"
import { RoleCheck } from "./shared/role-check"

// Covers are uploaded at any shape, so the box takes the standard ratio until
// the file loads; remembering each file's ratio keeps a revisit from moving.
const coverAspects = new Map<string, number>()

export function coverAspectFor(src: string): number {
	return coverAspects.get(src) ?? COVER_WIDTH / COVER_HEIGHT
}

export function rememberCoverAspect(src: string, image: HTMLImageElement) {
	if (!image.naturalWidth || !image.naturalHeight) return
	coverAspects.set(src, image.naturalWidth / image.naturalHeight)
}

const GLASS_BUTTON_CLASSES =
	"h-9 bg-gray-900/50 text-white shadow-sm backdrop-blur-lg hover:bg-gray-900/70 dark:bg-black/50 dark:text-white dark:hover:bg-black/70"

function isEditableElement(el: Element | null): boolean {
	if (!el) return false
	const tag = el.tagName
	if (tag === "INPUT" || tag === "TEXTAREA") return true
	if (el.getAttribute("contenteditable") === "true") return true
	if (el.closest(".ProseMirror")) return true
	return false
}

interface CoverImageProps {
	articleId: string
	imageUrl: string | null
	blurhash: string | null
	onUpdate?: (props: {
		imageUrl: string | null
		blurhash: string | null
	}) => void
	onIsUploadingChange?: (isUploading: boolean) => void
	/** When true, listens for document-level paste events to upload clipboard images as cover */
	globalPaste?: boolean
	/**
	 * Shown in place of the empty upload box; the upload hint appears on hover.
	 * It can be removed, which stores HIDDEN_ARTICLE_COVER, and brought back.
	 */
	placeholder?: ReactNode
}

interface CoverUploadOptions {
	withBackground: boolean
	successTitle: string
}

const loadImage = async (src: string) =>
	new Promise<HTMLImageElement>((resolve, reject) => {
		const img = new Image()
		img.onload = () => resolve(img)
		img.onerror = (...args) => reject(args)
		img.src = src
	})

const generateSize = (width: number, height: number): [number, number] => {
	const aspectRatio = width / height
	return aspectRatio > 1 ? [840, 840 / aspectRatio] : [840 * aspectRatio, 840]
}

const getImageData = (image: HTMLImageElement) => {
	const canvas = document.createElement("canvas")
	const [width, height] = generateSize(image.width, image.height)
	canvas.width = width
	canvas.height = height
	const context = canvas.getContext("2d")
	if (!context) {
		throw new Error("Could not get 2d context")
	}
	context.drawImage(image, 0, 0, width, height)
	return context.getImageData(0, 0, width, height)
}

const encodeImageToBlurhash = async (
	imageUrl: string,
): Promise<string | null> => {
	const start = new Date().getTime()
	try {
		const image = await loadImage(imageUrl)
		const imageData = getImageData(image)
		return encode(imageData.data, imageData.width, imageData.height, 4, 4)
	} catch (error) {
		return null
	} finally {
		const end = new Date().getTime()
		console.debug(`Encoding blurhash took ${end - start}ms`)
	}
}

export const CoverImage: FC<CoverImageProps> = ({
	imageUrl,
	blurhash,
	onUpdate,
	onIsUploadingChange,
	articleId,
	globalPaste,
	placeholder,
}) => {
	const { role } = useRole()
	const zero = useZero()

	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
	const [imagePreview, setImagePreview] = useState<any>(imageUrl)
	// A cover the browser already holds renders visible on the first frame, so
	// switching back to an article never replays the blur and fade.
	const [loadedSrc, setLoadedSrc] = useState<string | null>(() => {
		const cached = imageUrl ? readCachedImage(imageUrl) : null
		if (!imageUrl || !cached) return null
		rememberCoverAspect(imageUrl, cached)
		return imageUrl
	})
	const coverSrc = imagePreview ? String(imagePreview) : null
	const isCoverLoaded = coverSrc !== null && loadedSrc === coverSrc
	const [originalImageFile, setOriginalImageFile] = useState<File | null>(null)
	const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false)
	const [isUploading, setIsUploading] = useState(false)
	const imageInput = useRef<HTMLInputElement | null>(null)
	const getSignedUploadUrlMutation = api.file.getUrl.useMutation()

	const triggerFileSelection = useCallback(() => {
		if (role === "VIEWER") {
			return
		}
		imageInput.current?.click()
	}, [role])

	const uploadCover = useCallback(
		async (
			file: File,
			{ withBackground, successTitle }: CoverUploadOptions,
		) => {
			setIsUploading(true)
			onIsUploadingChange?.(true)
			try {
				const fileToUpload = withBackground
					? await processCoverImage(file)
					: file

				const objectUrl = URL.createObjectURL(fileToUpload)
				setImagePreview(objectUrl)

				const [{ uploadUrl, fileUrl }, blurhash] = await Promise.all([
					getSignedUploadUrlMutation.mutateAsync({
						fileName: fileToUpload.name,
						fileType: fileToUpload.type,
					}),
					encodeImageToBlurhash(objectUrl),
				])

				const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
				if (Number(filesize) < ALLOWED_FILE_SIZE_IN_MB) {
					const headers = { "Content-Type": fileToUpload.type }
					const res = await fetch(uploadUrl, {
						method: "PUT",
						body: fileToUpload,
						headers,
					})
					if (!res.ok) {
						throw new Error("File upload failed")
					}
					setIsBackgroundRemoved(!withBackground)
					onUpdate?.({ imageUrl: fileUrl, blurhash })
					toast.success({ title: successTitle })
				} else {
					toast.error({
						title: `Images need to be in jpg or png format and less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
					})
				}
			} catch (error) {
				toast.error({ title: "Something went wrong" })
				console.error("Image processing/upload error:", error)
			}
			setIsUploading(false)
			onIsUploadingChange?.(false)
		},
		[getSignedUploadUrlMutation, onIsUploadingChange, onUpdate],
	)

	// A new cover keeps its own pixels; the padded plate is opt-in.
	const onImageChange = useCallback(
		async (f: File) => {
			setOriginalImageFile(f)
			await uploadCover(f, {
				withBackground: false,
				successTitle: "Image uploaded",
			})
			if (imageInput.current) {
				imageInput.current.value = ""
			}
		},
		[uploadCover],
	)

	useEffect(() => {
		if (!globalPaste) return

		const handlePaste = (e: ClipboardEvent) => {
			if (isEditableElement(document.activeElement)) return
			if (isUploading) return

			const image = getClipboardFiles(e.clipboardData?.items).find((file) =>
				file.type.startsWith("image"),
			)
			if (image) {
				e.preventDefault()
				void onImageChange(image)
			}
		}

		document.addEventListener("paste", handlePaste)
		return () => document.removeEventListener("paste", handlePaste)
	}, [globalPaste, isUploading, onImageChange])

	const addBackground = (file: File) =>
		uploadCover(file, { withBackground: true, successTitle: "Image uploaded" })

	const removeBackground = (file: File) =>
		uploadCover(file, {
			withBackground: false,
			successTitle: "Background removed, using original image",
		})

	const validBlurhash = blurhash && isBlurhashValid(blurhash).result

	return (
		<div className="flex-1">
			{imagePreview ? (
				<div
					className={cn(
						"group relative overflow-hidden rounded-xl",
						!isCoverLoaded && !validBlurhash && "bg-gray-100 dark:bg-gray-800",
					)}
					style={{ aspectRatio: coverAspectFor(String(imagePreview)) }}
				>
					{validBlurhash && (
						<Blurhash
							hash={blurhash}
							// Fades out only once the image has faded in over it, so a
							// transparent cover never shows the blur behind it.
							className={cn(
								"pointer-events-none !absolute inset-0 overflow-hidden rounded-xl transition-opacity",
								isCoverLoaded
									? "opacity-0 delay-200 duration-150"
									: "opacity-100",
							)}
							width="100%"
							height="100%"
							resolutionX={32}
							resolutionY={32}
							punch={1}
						/>
					)}
					<img
						alt="cover"
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						src={imagePreview}
						onLoad={(e) => {
							if (!coverSrc) return
							rememberCoverAspect(coverSrc, e.currentTarget)
							setLoadedSrc(coverSrc)
						}}
						className={cn(
							"absolute inset-0 h-full w-full rounded-xl object-cover transition-opacity duration-200",
							isCoverLoaded ? "opacity-100" : "opacity-0",
						)}
					/>
					<RoleCheck not={["VIEWER"]}>
						<div className="absolute right-4 top-4 flex gap-2">
							{originalImageFile && !isBackgroundRemoved && (
								<Button
									variant="glass"
									size="lg"
									Icon={ImageMinus}
									iconStyle={{ width: "1rem", height: "1rem" }}
									iconClassName="text-white"
									className={GLASS_BUTTON_CLASSES}
									onClick={() => void removeBackground(originalImageFile)}
									disabled={isUploading}
									title="Remove background"
								>
									{isUploading ? "Processing..." : "Remove background"}
								</Button>
							)}
							{originalImageFile && isBackgroundRemoved && (
								<Button
									variant="glass"
									size="lg"
									Icon={Wand2}
									iconStyle={{ width: "1rem", height: "1rem" }}
									iconClassName="text-white"
									className={GLASS_BUTTON_CLASSES}
									onClick={() => void addBackground(originalImageFile)}
									disabled={isUploading}
									title="Add background"
								>
									{isUploading ? "Processing..." : "Add background"}
								</Button>
							)}
							<IconButton
								variant="glass"
								isRounded={true}
								size="lg"
								Icon={UploadIcon}
								iconClassName="h-4 w-4 !text-white"
								className={cn("h-9 w-9", GLASS_BUTTON_CLASSES)}
								onClick={triggerFileSelection}
								title="Upload new image"
							/>
							<IconButton
								variant="glass"
								isRounded={true}
								size="lg"
								Icon={TrashIcon}
								iconClassName="h-4 w-4 !text-white"
								className={cn("h-9 w-9", GLASS_BUTTON_CLASSES)}
								onClick={() => {
									if (role === "VIEWER") {
										return
									}
									setImagePreview(null)
									setOriginalImageFile(null)
									setIsBackgroundRemoved(false)
									void zero.mutate(
										mutators.helpCenterArticles.update({
											id: articleId,
											imageUrl: null,
										}),
									)
									onUpdate && onUpdate({ imageUrl: null, blurhash: null })
								}}
								title="Delete cover image"
							/>
						</div>
					</RoleCheck>
				</div>
			) : placeholder && isArticleCoverHidden(imageUrl) ? (
				<CoverUploadBox
					onSelect={triggerFileSelection}
					onRestore={
						role === "VIEWER"
							? undefined
							: () => onUpdate?.({ imageUrl: null, blurhash: null })
					}
				/>
			) : placeholder ? (
				<div className="group relative">
					<button
						onClick={triggerFileSelection}
						type="button"
						className="relative block w-full overflow-hidden rounded-lg"
					>
						{placeholder}
						<div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100">
							<PhotoPlusIcon className="h-[18px] w-[18px] text-white" />
							<p className="pt-2 font-medium text-white">Cover image</p>
							<span className="pt-1 text-xs font-normal text-white/80">
								Ideal dimensions are 1200 x 627
							</span>
						</div>
					</button>
					<RoleCheck not={["VIEWER"]}>
						<IconButton
							variant="glass"
							isRounded={true}
							size="lg"
							Icon={TrashIcon}
							iconClassName="h-4 w-4 !text-white"
							className={cn(
								"absolute right-4 top-4 h-9 w-9 opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100",
								GLASS_BUTTON_CLASSES,
							)}
							onClick={() =>
								onUpdate?.({ imageUrl: HIDDEN_ARTICLE_COVER, blurhash: null })
							}
							data-remove-generated-cover=""
							title="Delete cover image"
						/>
					</RoleCheck>
				</div>
			) : (
				<button
					onClick={triggerFileSelection}
					type="button"
					className="block w-full"
				>
					<div className="flex h-[20.625rem] flex-col items-center justify-center rounded-lg border border-gray-300 bg-gray-50 hover:opacity-75 dark:border-gray-700/60 dark:bg-gray-850/60">
						<PhotoPlusIcon className="h-[18px] w-[18px] text-tertiary" />
						<p className="pt-2 font-medium text-secondary">Cover image</p>
						<span className="pt-1 text-xs font-normal text-tertiary">
							Ideal dimensions are 1200 x 627
						</span>
					</div>
				</button>
			)}
			<input
				id="file"
				name="file"
				type="file"
				accept="image/*"
				ref={imageInput}
				className="hidden"
				onChange={(e) => {
					if (e.target.files?.[0]) {
						void onImageChange(e.target.files[0])
					}
				}}
			/>
		</div>
	)
}
